import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import CloseIcon from '@mui/icons-material/Close'
import DateRangeIcon from '@mui/icons-material/DateRange'
import CaleoDatePicker from 'Components/reusable/InputFields/CaleoDatePicker'
import { useContext } from 'react'
import { DatePickerProps } from 'react-datepicker'
import { formatEndDate, formatStartDate, getDateFormatByLocaleAndResolution } from 'utils/utils'
import { Context } from '../context'
import { AnyObject, ContextValue, EditableFieldsOfType, getFieldControlId } from '../types'

/**
 * Date field (with label/outlined variant).
 */
export const DataEditContextDatePickerField = <T extends AnyObject>({
  field,
  dateResolution,
  start,
  end,
  minDate,
  maxDate,
  isClearable,
  withTime = false,
}: {
  field: EditableFieldsOfType<T, Date | null>
  dateResolution?: 'day' | 'month' | 'year'
  minDate?: Date
  maxDate?: Date
  start?: boolean
  end?: boolean
  isClearable?: boolean
  withTime?: boolean
} & Omit<DatePickerProps, 'onChange' | 'dateResolution'>) => {
  const { flatData, disabled, errors, setFields, i18n, t } = useContext<ContextValue<T>>(Context)
  const controlId = getFieldControlId(field)

  const dateResolutionValue =
    dateResolution ??
    ((flatData as Record<string, unknown>)['dateResolution'] as 'day' | 'month' | 'year' | undefined) ??
    'month'

  const handleChange = (newValue: Date | null) => {
    if (newValue && !withTime) {
      if (start) {
        newValue = formatStartDate(newValue, dateResolutionValue)
      } else if (end) {
        newValue = formatEndDate(newValue, dateResolutionValue)
      }
    }
    setFields({ [field]: newValue })
  }

  const value = flatData[field] as unknown as Date | null

  let error = errors[field]

  if (!error && value) {
    if ((dateResolutionValue === 'day' || !dateResolutionValue) && minDate && value < minDate) {
      error = i18n.t('startDateEndDateError')
    }
    if (dateResolutionValue === 'month' && maxDate && value > maxDate) {
      error = i18n.t('endDateStartDateError')
    }
    if (dateResolutionValue === 'month') {
      const monthValue = new Date(value)
      monthValue.setDate(1)
      if (minDate) {
        const earliestSelectableMonth = new Date(minDate)
        earliestSelectableMonth.setDate(1)
        if (monthValue < earliestSelectableMonth) error = i18n.t('startDateEndDateError')
      }
      if (maxDate) {
        const latestSelectableMonth = new Date(maxDate)
        latestSelectableMonth.setDate(1)
        if (monthValue > latestSelectableMonth) error = i18n.t('endDateStartDateError')
      }
    }
    if (dateResolutionValue === 'year') {
      const yearValue = new Date(value)
      yearValue.setDate(1)
      yearValue.setMonth(0)
      if (minDate) {
        const earliestSelectableYear = new Date(minDate)
        earliestSelectableYear.setDate(1)
        earliestSelectableYear.setMonth(0)
        if (yearValue < earliestSelectableYear) error = i18n.t('startDateEndDateError')
      }
      if (maxDate) {
        const latestSelectableYear = new Date(maxDate)
        latestSelectableYear.setDate(1)
        latestSelectableYear.setMonth(0)
        if (yearValue > latestSelectableYear) error = i18n.t('endDateStartDateError')
      }
    }
  }

  const locale = i18n.language
  const format = getDateFormatByLocaleAndResolution(locale, dateResolutionValue)

  const handleClear = () => {
    setFields({ [field]: null })
  }

  return (
    <CaleoDatePicker
      id={controlId}
      selected={value}
      onChange={handleChange}
      dateFormat={format}
      showMonthYearPicker={dateResolutionValue === 'month'}
      showYearPicker={dateResolutionValue === 'year'}
      disabled={disabled[field]}
      minDate={minDate}
      maxDate={maxDate}
      customInput={
        <TextField
          data-testid={`field-date-picker-${controlId}`}
          aria-label={t(String(field))}
          disabled={disabled[field]}
          helperText={error}
          error={!!error}
          variant="standard"
          slotProps={{
            htmlInput: { 'aria-label': t(String(field)) },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {isClearable && value && (
                    <IconButton
                      data-testid={`button-date-picker-field-clear-${controlId}`}
                      onClick={handleClear}
                      onMouseDown={handleClear}
                      size="small"
                      sx={{ p: 0, m: 0 }}
                    >
                      <CloseIcon style={{ cursor: 'pointer' }} fontSize="small" />
                    </IconButton>
                  )}
                  <DateRangeIcon color="secondary" style={{ cursor: 'pointer' }} />
                </InputAdornment>
              ),
            },
          }}
        />
      }
      popperPlacement="bottom-start"
      showMonthDropdown
      useShortMonthInDropdown
      showYearDropdown
      withTime={withTime}
    />
  )
}

/**
 * Date resolution radio field (day / month / year).
 */
